import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import NotificationModal from './NotificationModal';
import { tenantApi } from '../../services/api';
import ClinicBrandLogo from '../common/ClinicBrandLogo';

const clinicReadKey = (s) => `qease_clinic_notif_read_${s || 'legacy'}`;

const ClinicAdminLayout = ({ children, activePage, title }) => {
    const { slug } = useParams();
    const base = slug ? `/${slug}/clinic-admin` : '/clinic-admin';
    const [clinicLabel, setClinicLabel] = useState("");
    const [clinicLogo, setClinicLogo] = useState("");
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [profileData, setProfileData] = useState({
        name: 'Admin',
        email: 'admin@example.com',
        phone: '(+62) 812-3456-7890',
        role: 'Clinic Admin'
    });
    const navigate = useNavigate();


    const [clinicNotifications, setClinicNotifications] = useState([]);

    React.useEffect(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            const name = u?.klinik?.nama_klinik;
            const logo = u?.klinik?.logo_url || "";
            setClinicLabel(name || slug || "");
            setClinicLogo(logo);
        } catch {
            setClinicLabel(slug || "");
            setClinicLogo("");
        }
    }, [slug]);

    React.useEffect(() => {
        const handleNewNotification = (e) => {
            const newNotif = {
                id: `evt-${Date.now()}`,
                type: e.detail.type || 'info',
                title: e.detail.title,
                message: e.detail.message,
                time: 'Baru saja',
                isRead: false,
                icon: e.detail.icon || 'material-symbols:info-outline',
                color: e.detail.color || 'bg-blue-500',
            };
            setClinicNotifications((prev) => [newNotif, ...prev]);
        };
        window.addEventListener('addNotification', handleNewNotification);
        return () => window.removeEventListener('addNotification', handleNewNotification);
    }, []);

    const readIdSet = useCallback(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem(clinicReadKey(slug)) || '[]'));
        } catch {
            return new Set();
        }
    }, [slug]);

    const fetchClinicNotifications = useCallback(async () => {
        if (!slug) return;
        if (!localStorage.getItem('token')) return;
        try {
            const res = await tenantApi(slug).notificationFeed();
            if (!res.data?.success) return;
            const read = readIdSet();
            setClinicNotifications((prev) => {
                const manual = prev.filter((n) => String(n.id).startsWith('evt-'));
                const apiRows = (res.data.data || []).map((n) => ({
                    id: n.id,
                    type: n.type || 'info',
                    title: n.title,
                    message: n.message,
                    time: n.time || '',
                    isRead: read.has(n.id),
                    icon: n.icon || 'material-symbols:info-outline',
                    color: n.color || 'bg-[#06B6D4]',
                }));
                const seen = new Set(apiRows.map((a) => a.id));
                const manualFiltered = manual.filter((m) => !seen.has(m.id));
                return [...manualFiltered, ...apiRows];
            });
        } catch (e) {
            // Token expired / missing: do not spam console or block page UI.
            if (e?.response?.status === 401) return;
            // Keep layout functional even when notification endpoint fails.
        }
    }, [slug, readIdSet]);

    useEffect(() => {
        if (!slug) return undefined;
        fetchClinicNotifications();
        const t = setInterval(fetchClinicNotifications, 40000);
        return () => clearInterval(t);
    }, [slug, fetchClinicNotifications]);

    const markAllClinicNotificationsRead = useCallback(() => {
        if (!slug) return;
        const ids = clinicNotifications.map((n) => n.id);
        const merged = new Set([...readIdSet(), ...ids]);
        localStorage.setItem(clinicReadKey(slug), JSON.stringify([...merged]));
        setClinicNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    }, [slug, clinicNotifications, readIdSet]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate(slug ? `/${slug}/login` : 'login');
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-jakarta">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-gray-100 flex flex-col fixed h-screen z-50">
                <div className="p-8">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <ClinicBrandLogo
                                logoUrl={clinicLogo}
                                clinicName={clinicLabel || slug}
                                fallbackText="Clinic"
                                imageClassName="w-24 h-auto object-contain"
                                textClassName="text-lg font-bold text-gray-800"
                            />
                        </div>
                        {(clinicLabel || slug) && (
                            <p
                                className="text-[11px] font-semibold text-gray-400 truncate max-w-[9.5rem] mt-1"
                                title={clinicLabel || slug}
                            >
                                {clinicLabel || slug}
                            </p>
                        )}
                    </div>
                </div>

                <nav className="flex-1 px-4 space-y-2 overflow-y-auto no-scrollbar">
                    <Link to={base} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'dashboard' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:dashboard-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Dashboard</span>
                    </Link>
                    <Link to={`${base}/poli`} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'poli' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:grid-view-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Poli</span>
                    </Link>
                    <Link to={`${base}/pasien`} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'pasien' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:person-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Pasien</span>
                    </Link>
                    <Link to={`${base}/antrian`} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'antrian' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:list-alt-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Antrian</span>
                    </Link>
                    <Link to={`${base}/dokter`} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'dokter' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:medical-services-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Dokter</span>
                    </Link>
                    <Link to={`${base}/settings`} className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'settings' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:settings-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Pengaturan</span>
                    </Link>

                    <div className="pt-4 mt-4 border-t border-gray-100">
                        <button 
                            onClick={() => {
                                const user = JSON.parse(localStorage.getItem('user') || '{}');
                                const s = slug || user.klinik?.slug;
                                if (s) {
                                    window.open(`/${s}`, '_blank');
                                } else {
                                    alert('Slug klinik tidak ditemukan.');
                                }
                            }}
                            className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-[#06B6D4] bg-cyan-50 hover:bg-[#06B6D4] hover:text-white transition-all group"
                        >
                            <Icon icon="material-symbols:open-in-new" className="text-xl group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-sm">Lihat Landing Page</span>
                        </button>
                    </div>

                    <button 
                        onClick={() => setShowLogoutModal(true)}
                        className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all mt-4"
                    >
                        <Icon icon="material-symbols:logout" className="text-xl" />
                        <span className="font-semibold text-sm">Logout</span>
                    </button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 ml-64 p-8">
                {/* Header */}
                <header className="flex justify-between items-center mb-8">
                    <div className="relative w-96 font-body">
                        <Icon icon="material-symbols:search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                        <input
                            type="text"
                            placeholder="Search"
                            className="w-full bg-white border border-gray-100 rounded-full py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 transition-all shadow-sm"
                        />
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="relative">
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="w-10 h-10 bg-white border border-gray-50 rounded-full flex items-center justify-center text-gray-500 shadow-sm cursor-pointer hover:bg-gray-50 transition-all relative"
                            >
                                <Icon icon="material-symbols:notifications-outline" className="text-xl" />
                                {clinicNotifications.filter((n) => !n.isRead).length > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                                        {Math.min(99, clinicNotifications.filter((n) => !n.isRead).length)}
                                    </span>
                                )}
                            </button>

                        </div>

                        <div className="relative">
                            <div
                                className="flex items-center gap-4 pl-6 border-l border-gray-200 cursor-pointer hover:bg-gray-50/50 py-1 transition-all rounded-lg"
                                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                            >
                                <div className="text-right">
                                    <p className="text-sm font-bold text-gray-800">{profileData.name}</p>
                                    <p className="text-xs text-gray-400">{profileData.role}</p>
                                </div>
                                <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex items-center justify-center bg-gray-100">
                                    <Icon icon="material-symbols:account-circle" className="text-3xl text-gray-400" />
                                </div>
                                <Icon
                                    icon="material-symbols:keyboard-arrow-down"
                                    className={`text-gray-400 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </div>

                            {/* Profile Dropdown */}
                            {isProfileDropdownOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setIsProfileDropdownOpen(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20 animate-slideDown origin-top-right">
                                        <div className="mx-2 my-1 border-t border-gray-50" />
                                        <button
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            onClick={() => {
                                                setIsProfileDropdownOpen(false);
                                                setShowLogoutModal(true);
                                            }}
                                        >
                                            <Icon icon="material-symbols:logout" className="text-lg" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
                    <div className="flex items-center gap-4">



                    </div>
                </div>

                {children}
            </main>

            {/* Logout Confirmation Modal */}
            {
                showLogoutModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                            onClick={() => setShowLogoutModal(false)}
                        ></div>

                        {/* Modal Card */}
                        <div className="bg-white rounded-[40px] w-full max-w-sm p-10 relative z-10 shadow-2xl transform transition-all flex flex-col items-center text-center">
                            {/* Icon */}
                            <div className="w-24 h-24 bg-[#FF6B6B] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                                <span className="text-white text-5xl font-bold leading-none -mt-1">!</span>
                            </div>

                            {/* Text Content */}
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Anda Yakin?</h3>
                            <p className="text-gray-500 text-sm font-medium mb-10 leading-relaxed px-4">
                                Apakah anda yakin keluar dari akun ini
                            </p>

                            {/* Buttons */}
                            <div className="w-full space-y-4">
                                <button
                                    onClick={handleLogout}
                                    className="w-full py-4 bg-[#FF6B6B] text-white font-bold rounded-2xl shadow-lg shadow-red-500/20 hover:bg-[#ff5252] transition-colors active:scale-95"
                                >
                                    Ya, Keluar
                                </button>
                                <button
                                    onClick={() => setShowLogoutModal(false)}
                                    className="w-full py-4 bg-white text-gray-500 font-bold rounded-2xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors active:scale-95"
                                >
                                    Tidak
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Notification Modal */}
            {showNotifications && (
                <NotificationModal
                    isOpen={showNotifications}
                    onClose={() => setShowNotifications(false)}
                    notifications={clinicNotifications}
                    onMarkAllRead={slug ? markAllClinicNotificationsRead : undefined}
                />
            )}

            <style>{`
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slideDown { animation: slideDown 0.2s ease-out; }
            `}</style>
        </div>
    );
};

export default ClinicAdminLayout;
